/**
 * The reading pane's fixed footer: Focus and Prune — the two acts on the
 * selected branch — and, right-aligned, the exploration's account in one quiet
 * line: the model passes, and the figure against the cap. The sheet's footer,
 * with this app's live arithmetic behind the numbers. The node count it led with
 * is the findings tray's now, at the head of the same column (D346): one number,
 * printed twice a column apart under two names, was one too many.
 *
 * Its own file rather than a private of the pane's shell, because the footer
 * outlives the shell it was written in: it anchored the inspector's rail and it
 * anchors the thread column the same way, and a footer defined inside either
 * would have to move house every time the pane does.
 */

import { useMemo } from 'react'
import { useStore } from '../core/store'
import { estimateSpendUsd, fmtUsd } from '../core/models'
import { parseCap } from '../core/ledger'
import { Icon } from './components/Icon'
import { prunePress } from './Coach'
import { SPEND_TITLE } from './VerbPrice'

/**
 * The exploration's account in one line: the model passes, and the figure
 * against the cap. The foot's, and since D353 the docked composer's too, where
 * the owner's design puts it under the box — one sentence from one place, so
 * the two homes cannot come to say it two ways.
 */
export function useAccount(): string {
  const session = useStore((s) => s.session)
  const rev = useStore((s) => s.rev)
  const runCeiling = useStore((s) => s.runCeiling)
  const live = useMemo(() => estimateSpendUsd(session.spend), [session, rev])
  const cap = parseCap(runCeiling)
  // The narrator's voice turn after each pass is an op on the ledger, but not a
  // pass anybody pressed for: counted, the footer said twelve passes where the
  // person had asked for six.
  const passes = session.ops.filter((o) => !o.narration).length
  // A copy opened from a link and not yet continued: the figure is what its
  // sender's passes were estimated at, and printed here it read as the reader's
  // own spend. The read-first line leads until Continue working; then the ledger
  // is theirs to add to, and the figure comes back.
  const readFirst = !!session.shared?.readFirst
  // "est. … so far", not "spent": the figure is the estimate that leans high,
  // and "$2.44 spent" stated it as money that had left the account.
  return `${passes} model pass${passes === 1 ? '' : 'es'}${
    readFirst
      ? ''
      : cap !== null
        ? ` · est. ${fmtUsd(live.usd)} of ${fmtUsd(cap)}`
        : live.usd > 0
          ? ` · est. ${fmtUsd(live.usd)} so far`
          : ''
  }`
}

export function ThreadFoot({
  gutter = false,
  account: showAccount = true,
}: {
  gutter?: boolean
  /** Whether the foot prints the account. Not where the docked composer under
   *  it already does (D353): one line, said once. */
  account?: boolean
}) {
  const session = useStore((s) => s.session)
  const selectedId = useStore((s) => s.selectedId)
  const focusId = useStore((s) => s.focusId)
  const setFocus = useStore((s) => s.setFocus)
  const busy = useStore((s) => s.busy)
  const account = useAccount()

  const node = selectedId ? session.nodes[selectedId] : null
  const focused = node !== null && focusId === node.id

  return (
    // The gutter is the mount's to ask for, because the pane supplies none of
    // its own (Thread.tsx): pinned to a desktop column the foot is a region of
    // the pane and stands in its 20px, as the head above it does (the owner's
    // design, D356; the log stands further in, D365); on a phone it scrolls
    // inside the feed, whose padding there is that gutter.
    <div className="insp-foot" style={gutter ? { marginInline: 20 } : undefined}>
      <button
        className={focused ? 'btn btn--on' : 'btn btn--outline'}
        disabled={!node}
        onClick={() => node && setFocus(focused ? null : node.id)}
        title={
          node
            ? 'Dim everything outside this node’s lineage and children'
            : 'Select a node to focus its branch'
        }
      >
        <Icon name="target" size={13} />
        {focused ? 'Unfocus' : 'Focus'}
      </button>
      <button
        className="btn btn--outline insp-foot-prune"
        disabled={busy || !node || node.parentId === null}
        // The gesture itself lives in `Coach.tsx`, because the coach's `prune`
        // cue must press EXACTLY this — the mark, the count, and the toast whose
        // undo is checked against the history entry it belongs to. One writer.
        onClick={() => node && prunePress(node.id)}
        // Every reason it is greyed, not only the seed's: a title is what a
        // screen reader reads beside a disabled button, and "Remove this branch"
        // under a pass in flight said what it would do, not why it would not.
        title={
          !node
            ? 'Select a node to prune its branch'
            : node.parentId === null
              ? 'The seed cannot be pruned'
              : busy
                ? 'Not while a pass is in flight — prune when it lands'
                : 'Remove this branch — undoable'
        }
      >
        <Icon name="trash" size={13} />
        Prune
      </button>
      {showAccount && (
        <span className="insp-foot-meta" title={SPEND_TITLE}>
          {account}
        </span>
      )}
    </div>
  )
}
